import { isEqual } from 'lodash-es'

import {
  CartLine,
  GetCartVariantsQuery,
  GetZidiCartVariantsQuery,
  ProductCatalogVariantGroup,
} from '../../../__generated__/graphql/catalog/graphql'
import { createPdpUrl } from '../../../app/_components/navigation/createPdpUrl'
import { CartStoreLineItem } from '../../../lib/adapters/cart-persistence/useCartStore'
import { getDollarsAndCentsForAmount } from '../../../lib/utils/currency'
import { CartItem, HydratedCartItemFields } from '../../../types/figs'

import { UseCartNewCartItem } from '.'

type ExternalCartItem = Omit<CartStoreLineItem, 'cost' | 'discounts'>

type VariantTypes =
  | NonNullable<GetZidiCartVariantsQuery['variants']>['nodes'][number]
  | GetCartVariantsQuery['productCatalogVariants'][number]

type VariantsBySku = Record<string, VariantTypes>

export function augmentPersistedCartItemsWithFigsData(
  persistedCartItems: ExternalCartItem[],
  variantsBySku: VariantsBySku,
  locale: string
): CartItem[] {
  return persistedCartItems.map((item): CartItem => {
    return augmentCartStoreLineItem(item, variantsBySku, locale)
  })
}

export const blankCartItem = {
  isLowInventory: false,
  image: {
    alt: '',
    url: undefined,
  },
  fullPrice: 0,
  effectivePrice: 0,
  finalSale: false,
  productGroupHandle: '',
  productType: '',
  title: '',
  variantId: '',
  colorId: undefined,
  colorDisplayValue: undefined,
  fitId: undefined,
  fitDisplayValue: undefined,
  sizeId: undefined,
  sizeDisplayValue: undefined,
  shopifyProductId: '',
  shopifyVariantId: '',
  currencyCode: 'USD',
  gender: '',
  swPromoEligible: true,
  categoryId: '',
  availableForSale: true,
  urlPath: '#',
} satisfies Partial<CartItem>

function getBasicFields(item: ExternalCartItem): CartItem {
  return {
    ...blankCartItem,
    key: item.cartLineId,
    cartStoreKey: item.cartLineId,
    sku: item.sku,
    quantity: item.quantity,
    properties: item.attributes.reduce((accumulator, current) => {
      return Object.assign(accumulator, { [current.key]: current.value })
    }, {}),
  }
}

function getHydratedFields(
  variantData: VariantTypes,
  discountMultiplier: number,
  locale: string
): HydratedCartItemFields {
  if ('__typename' in variantData && variantData.__typename === 'ProductCatalogVariant') {
    return {
      colorId: variantData.group.color?.id,
      colorDisplayValue: variantData.group.color?.displayValue,
      image: {
        alt: variantData.group.title,
        url: variantData.images[0]?.url,
      },
      fitDisplayValue: variantData.fit?.id === 'regular' ? null : variantData.fit?.displayValue,
      fitId: variantData.fit?.id,
      fullPrice: getDollarsAndCentsForAmount({
        value: variantData.compareAtPrice?.amount ?? variantData.price.amount,
        unit: 'dollars',
        currency: variantData.price.currencyCode,
        locale,
      }).cents,
      shopifyProductId: variantData.shopifyProductId,
      effectivePrice: getDollarsAndCentsForAmount({
        value:
          !!variantData.compareAtPrice?.amount && discountMultiplier < 1
            ? variantData.compareAtPrice.amount * discountMultiplier
            : variantData.price.amount * discountMultiplier,
        unit: 'dollars',
        currency: variantData.price.currencyCode,
        locale,
      }).cents,
      productGroupHandle: variantData.group.product.handle,
      sizeId: variantData.size?.id,
      sizeDisplayValue: variantData.size?.displayValue,
      title: variantData.group.title,
      finalSale: variantData.group.finalSale,
      categoryId: variantData.group.primaryCategory.analyticsId,
      currencyCode: variantData.price.currencyCode,
      gender: variantData.group.gender,
      availableForSale: variantData.availability !== 'UNAVAILABLE',
      urlPath: variantData.urlPath,
      shopifyVariantId: variantData.shopifyId,
      swPromoEligible: variantData.group.siteWidePromoEligible,
      isLowInventory: variantData.stockState === 'LOW_STOCK',
    }
  } else {
    const price =
      variantData.priceDetails?.discountPrice?.amount ?? variantData.priceDetails!.price!.amount
    const effectivePrice = Math.round(price * discountMultiplier)

    return {
      colorId: variantData.rawColor,
      colorDisplayValue: variantData.color,
      image: {
        alt: variantData.productGroup.title,
        url: variantData.image?.source,
      },
      fitId: variantData.rawFit,
      fitDisplayValue: variantData.rawFit === 'regular' ? null : variantData.fit,
      fullPrice: variantData.priceDetails!.price!.amount,
      shopifyVariantId: variantData.shopifyId,
      shopifyProductId: variantData.externalParentId,
      effectivePrice,
      productGroupHandle: variantData.productGroup.handle,
      sizeId: variantData.rawSize,
      sizeDisplayValue: variantData.size,
      title: variantData.productGroup.title,
      finalSale: variantData.product.finalSale,
      categoryId: variantData.productGroup.rawCategory,
      currencyCode: variantData.priceDetails!.price!.currency,
      gender: variantData.productGroup.genderCategory,
      availableForSale: variantData.product.availableForSale,
      swPromoEligible: variantData.product.swPromoEligible,
      urlPath: createPdpUrl({
        productGroupHandle: variantData.productGroup.handle,
        collectionHandle: undefined,
        isKit: false,
        isExclusive: variantData.product.exclusive,
        genderKey: variantData.productGroup.genderCategory,
        colorKey: variantData.rawColor,
        fitKey: variantData.rawFit,
        sizeKey: variantData.rawSize,
      }),
      isLowInventory: variantData.isLowInventory,
    }
  }
}

function augmentCartStoreLineItem(
  item: ExternalCartItem,
  variantsBySku: VariantsBySku,
  locale: string
): CartItem {
  const variant = variantsBySku[item.sku]
  const basicFields = getBasicFields(item)
  if (!variant) {
    return basicFields
  }

  const discountPct = parseFloat(
    item?.attributes.find(e => e.key === '_kitDiscountPct')?.value ?? ''
  )
  const validDiscountPct = !isNaN(discountPct) && discountPct > 0 && discountPct <= 100
  const discountMultiplier = validDiscountPct ? (100 - discountPct) / 100 : 1
  const hydratedFields = getHydratedFields(variant, discountMultiplier, locale)

  return {
    ...basicFields,
    variantId: hydratedFields.shopifyVariantId,
    productType: hydratedFields.categoryId,
    ...hydratedFields,
  }
}

export function areCartLinesEqual(
  itemA: Omit<CartLine, 'cost' | 'discountAllocations' | 'id' | 'quantity'>,
  itemB: Omit<CartLine, 'cost' | 'discountAllocations' | 'id' | 'quantity'>
): boolean {
  return isEqual(
    {
      attributes: itemA.attributes?.filter(attr => {
        return attr?.key !== '__typename' && attr?.key !== '_collectionAttribution'
      }),
      sku: itemA.sku,
    },
    {
      attributes: itemB.attributes?.filter(attr => {
        return attr?.key !== '__typename' && attr?.key !== '_collectionAttribution'
      }),
      sku: itemB.sku,
    }
  )
}

type ApiData = {
  locale: string
  product: {
    handle: string
  }
  variantGroup: {
    siteWidePromoEligible: boolean
    finalSale: boolean
    primaryCategory: Pick<ProductCatalogVariantGroup['primaryCategory'], 'analyticsId'>
  }
  variant: {
    shopifyId: string
    shopifyProductId: string
    sku: string
    price: {
      amount: number
      currencyCode: string
    }
    compareAtPrice?: null | {
      amount: number
    }
  }
  options?: {
    properties?: Record<string, string | undefined>
    quantity?: number
    isPortalColor?: boolean
    isPortalLogo?: boolean
  }
}

export const convertApiDataToNewCartItem = (data: ApiData): UseCartNewCartItem => {
  return {
    finalSale: data.variantGroup.finalSale,
    swPromoEligible: data.variantGroup.siteWidePromoEligible,
    isPortalColor: data.options?.isPortalColor,
    isPortalLogo: data.options?.isPortalLogo,
    quantity: data.options?.quantity,
    properties: data.options?.properties,
    item: {
      shopifyId: data.variant.shopifyId,
      externalParentId: data.variant.shopifyProductId,
      currencyType: data.variant.price.currencyCode,
      handle: data.product.handle,
      sku: data.variant.sku,
      category: data.variantGroup.primaryCategory.analyticsId,
      price: getDollarsAndCentsForAmount({
        value: data.variant.compareAtPrice?.amount ?? data.variant.price.amount,
        unit: 'dollars',
        currency: data.variant.price.currencyCode,
        locale: data.locale,
      }).cents,
      discountPrice: data.variant.compareAtPrice?.amount
        ? getDollarsAndCentsForAmount({
            value: data.variant.price.amount,
            unit: 'dollars',
            currency: data.variant.price.currencyCode,
            locale: data.locale,
          }).cents
        : null,
    },
  }
}
